import { ReactNode, useEffect, useState } from "react";

/**
 * Shared shell for the small ambient easter eggs scattered around the site —
 * the cat on the Dick page, the square on the Author Matrix, and so on.
 *
 * Everything that is true of ALL of them lives here, on purpose. When this was
 * one component it carried its own reduced-motion guard, rail sizing, tooltip
 * and pause-on-hover; copying that per egg guarantees they drift apart. Each
 * egg should be an SVG and a line of copy, nothing more.
 *
 * Hard-won constraints baked in, all of which were real bugs:
 *
 *  - The track needs real width. A box whose only child is absolutely
 *    positioned has no in-flow content and collapses to zero in a flex parent,
 *    which silently freezes everything inside it.
 *
 *  - Pacing animates `left` against a rail sized to (100% - artwork), NOT
 *    translateX. A percentage inside translateX resolves against the element's
 *    own box, and translateX(calc(100% - Npx)) teleports instead of
 *    interpolating. Sizing the rail is what keeps calc() out of the keyframes.
 *
 *  - The facing flip lives on its own span. Sharing a transform with the
 *    traverse mirrors the tooltip for half of every lap.
 *
 *  - NEVER put a backtick in the style block below. It sits inside a template
 *    literal, so one backtick ends the string and the file stops compiling —
 *    and Vite answers a failed compile by serving the last good bundle, so the
 *    page still looks right while you debug something that is not running.
 *
 * Reveal policy is always-on: the eggs are visible whenever the page is, rather
 * than rationed by visit count. They are quiet enough to live with, and an egg
 * nobody ever sees is just dead code.
 */

export type AmbientEggProps = {
  /** Tooltip copy, and the accessible name. Should say why it is here. */
  label: string;
  /** Artwork width in px. The rail is sized against this, so it must be right. */
  width: number;
  /** Track height in px. */
  height: number;
  /** The artwork, normally an inline svg. */
  children: ReactNode;
  /**
   * pace — walks the width of the column and turns at each end.
   * hold — stays put and animates in place (the child owns that animation).
   */
  motion?: "pace" | "hold";
  /** One full there-and-back lap, in seconds. Ignored when motion is hold. */
  durationSeconds?: number;
  /** Where a held egg sits. Ignored when motion is pace. */
  align?: "left" | "center" | "right";
  /**
   * Which side the tooltip opens on. Defaults to above, but an egg tucked under
   * a paragraph needs "bottom" or the tooltip lands in the body copy.
   */
  tip?: "top" | "bottom";
};

const AmbientEgg = ({
  label,
  width,
  height,
  children,
  motion = "pace",
  durationSeconds = 30,
  align = "left",
  tip = "top",
}: AmbientEggProps) => {
  const [reducedMotion, setReducedMotion] = useState(true);
  // Tap-to-reveal for touch, which has no hover. Hover and keyboard focus are
  // handled in CSS so they work before React has hydrated.
  const [tapped, setTapped] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      setReducedMotion(false);
      return;
    }
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // A thing crossing the screen is exactly what the setting asks us not to do,
  // and a stationary egg parked mid-page reads as a mistake rather than a joke.
  if (reducedMotion) return null;

  const paces = motion === "pace";
  const lap = `${durationSeconds}s`;
  // Right-align has to anchor on `right`, not `left: 100%` — the latter puts the
  // artwork's LEFT edge at the rail's right edge, i.e. entirely outside it.
  const held: React.CSSProperties =
    align === "right"
      ? { right: 0 }
      : align === "center"
        ? { left: "50%", transform: "translateX(-50%)" }
        : { left: 0 };

  return (
    <div
      className="ambient-egg-track relative w-full select-none"
      style={{ height }}
      data-tapped={tapped}
    >
      <style>{`
        /* Pacing runs on left, against a rail already narrowed by the artwork
           width, so 100% lands flush with the right edge and never past it. */
        @keyframes ambient-egg-pace {
          0%   { left: 0%; }
          46%  { left: 100%; }
          50%  { left: 100%; }
          96%  { left: 0%; }
          100% { left: 0%; }
        }
        /* Turning at each end. Interpolating through scaleX(0) is what makes it
           read as a turn rather than a jump cut. */
        @keyframes ambient-egg-turn {
          0%, 46%  { transform: scaleX(1); }
          50%, 96% { transform: scaleX(-1); }
          100%     { transform: scaleX(1); }
        }

        .ambient-egg-pace { animation: ambient-egg-pace linear infinite; }
        .ambient-egg-turn { animation: ambient-egg-turn linear infinite; }

        /* It stops when you point at it. Without this you would be chasing a
           moving target to read its own tooltip. */
        .ambient-egg-track:hover .ambient-egg-btn,
        .ambient-egg-track:hover .ambient-egg-btn *,
        .ambient-egg-track:focus-within .ambient-egg-btn,
        .ambient-egg-track:focus-within .ambient-egg-btn *,
        .ambient-egg-track[data-tapped="true"] .ambient-egg-btn,
        .ambient-egg-track[data-tapped="true"] .ambient-egg-btn * {
          animation-play-state: paused;
        }

        .ambient-egg-tip { opacity: 0; transition: opacity 180ms ease; }
        .ambient-egg-btn:hover .ambient-egg-tip,
        .ambient-egg-btn:focus-visible .ambient-egg-tip,
        .ambient-egg-track[data-tapped="true"] .ambient-egg-tip {
          opacity: 1;
        }
        .ambient-egg-btn:hover .ambient-egg-ink,
        .ambient-egg-btn:focus-visible .ambient-egg-ink,
        .ambient-egg-track[data-tapped="true"] .ambient-egg-ink {
          opacity: 1;
        }
      `}</style>

      <div
        className="absolute inset-y-0 left-0 pointer-events-none"
        style={{ width: paces ? `calc(100% - ${width}px)` : "100%" }}
      >
        <button
          type="button"
          onClick={() => setTapped((v) => !v)}
          onBlur={() => setTapped(false)}
          aria-label={label}
          style={paces ? { animationDuration: lap } : held}
          className={`ambient-egg-btn pointer-events-auto absolute top-0 cursor-default rounded
                      focus:outline-none focus-visible:ring-1 focus-visible:ring-cyan-400/60
                      ${paces ? "ambient-egg-pace left-0" : ""}`}
        >
          <span
            className={paces ? "ambient-egg-turn block" : "block"}
            style={paces ? { animationDuration: lap } : undefined}
          >
            {/* Artwork sits at 45% and lifts to full on hover/focus/tap, which
                is what makes it feel like it noticed you. */}
            <span className="ambient-egg-ink block opacity-[0.45] transition-opacity duration-200">
              {children}
            </span>
          </span>

          {/* Outside the turn span, so it is never mirrored. Width is clamped
              against the viewport so it cannot push the page sideways when the
              egg is near either edge. */}
          <span
            className={`ambient-egg-tip pointer-events-none absolute left-1/2
                       -translate-x-1/2 w-max max-w-[min(14rem,70vw)]
                       rounded border border-cyan-400/20 bg-slate-950/95 px-2 py-1
                       text-[11px] leading-snug text-slate-300 text-center shadow-lg
                       ${tip === "bottom" ? "top-full mt-2.5" : "bottom-full mb-2.5"}`}
          >
            {label}
          </span>
        </button>
      </div>
    </div>
  );
};

export default AmbientEgg;
